import type { ProductSearchDocument, SearchGroup } from '../api/search';
import type { AssetNote, OutOfScopeFlag, OutOfScopeReason } from '../api/types';
import { meaningful } from './meaningful';
import { sortGrades } from './size-order';

/** Uma grade disponível do modelo — e o id que entra no lote ao tocá-la. */
export interface SearchGrade {
  productId: string;
  label: string;
}

/**
 * Uma LINHA da busca: um modelo (marca + descrição + cor) com as grades em
 * estoque.
 *
 * Não é um `LotItem` e nem tenta ser. O hit do índice tem nomes próprios
 * (`display_grade`, `in_stock`) e a linha tem uma ação que a peça do lote não
 * tem — escolher o tamanho antes de adicionar.
 */
export interface SearchResultModel {
  /** `model_key` — chave estável da linha. */
  key: string;
  brand: string | null;
  description: string;
  /** Categoria normalizada: alimenta o ícone quando não há foto. */
  category: string;
  color: string | null;
  grades: SearchGrade[];
  /** Menor preço entre as grades do grupo. */
  salePrice: number;
  /**
   * As grades do grupo não custam o mesmo. O `model_key` não inclui preço, e
   * peças iguais entram na base com valores diferentes; sem isto a linha
   * mostraria um preço que o cliente não pagaria ao escolher outro tamanho.
   */
  priceVaries: boolean;
  retailPrice: number | null;
  assetsUrls: string[];
  /** Vazio no caso comum: só as fotos que não são da peça exata têm legenda. */
  assetsNotes: AssetNote[];
  /**
   * Modelo do estoque próprio. Vem do fornecedor, então todas as grades do
   * grupo concordam — basta o primeiro documento.
   */
  readyDelivery: boolean;
}

const FALLBACK_DESCRIPTION = 'Peça sem descrição';
/** Peça sem grade cadastrada ainda precisa de um chip para ser adicionada. */
const FALLBACK_GRADE = 'Único';

function gradeLabel(doc: ProductSearchDocument): string {
  return meaningful(doc.display_grade) ?? FALLBACK_GRADE;
}

/**
 * Grades do grupo, deduplicadas e na ordem em que se lê um tamanho.
 *
 * A dedup é necessária: `model_key` é marca+descrição+cor, então dois SKUs da
 * mesma peça no mesmo tamanho caem no mesmo grupo. O primeiro id vence — os
 * dois levam à mesma peça, e o cliente leva no máximo uma unidade por SKU.
 */
function collectGrades(docs: ProductSearchDocument[]): SearchGrade[] {
  const byLabel = new Map<string, string>();
  for (const doc of docs) {
    const label = gradeLabel(doc);
    if (!byLabel.has(label)) byLabel.set(label, doc.id);
  }
  const ordered = sortGrades(byLabel.keys());
  return ordered.map((label) => ({
    label,
    productId: byLabel.get(label) as string,
  }));
}

function adaptGroup(group: SearchGroup): SearchResultModel | null {
  const docs = group.hits.map((hit) => hit.document).filter(Boolean);
  const first = docs[0];
  if (!first) return null;

  const prices = docs.map((d) => d.suggested_sale_price);
  const salePrice = Math.min(...prices);
  // Etiqueta do SKU mais barato, que é o preço exibido: cruzar o "de" de uma
  // peça com o "por" de outra inventaria um desconto que não existe.
  const cheapest =
    docs.find((d) => d.suggested_sale_price === salePrice) ?? first;
  const retail = cheapest.retail_price;

  return {
    key: group.group_key.join('|') || first.model_key,
    brand: meaningful(first.brand),
    description: meaningful(first.description) ?? FALLBACK_DESCRIPTION,
    category: first.category_normalized ?? '',
    color: meaningful(first.color_description) ?? meaningful(first.color),
    grades: collectGrades(docs),
    salePrice,
    priceVaries: prices.some((p) => p !== salePrice),
    retailPrice: retail !== undefined && retail > 0 ? retail : null,
    assetsUrls: cheapest.assets_urls ?? first.assets_urls ?? [],
    // Do MESMO documento de onde vieram as fotos: como as legendas casam
    // por URL, as do outro documento do grupo não descreveriam nenhuma
    // das imagens exibidas.
    assetsNotes: cheapest.assets_urls
      ? cheapest.assets_notes ?? []
      : first.assets_notes ?? [],
    readyDelivery: first.ready_delivery === true,
  };
}

/** Grupos do Typesense → linhas da busca, na ordem de relevância que veio. */
export function adaptSearchGroups(groups: SearchGroup[]): SearchResultModel[] {
  return groups
    .map(adaptGroup)
    .filter((model): model is SearchResultModel => model !== null);
}

/** Todos os ids visíveis — é o que vai ao `/items/validate`. */
export function productIdsOf(models: SearchResultModel[]): string[] {
  return models.flatMap((model) => model.grades.map((g) => g.productId));
}

/**
 * Sinalização da LINHA: união dos motivos das grades, um por motivo.
 *
 * O veredito é por `product_id`, e as grades de um mesmo modelo divergem em
 * pelo menos um motivo (`size_not_selected`). Repetir a sinalização por chip
 * encheria a linha de texto para dizer a mesma coisa; unir e colapsar mantém
 * a linha legível ao custo de o nível 3 valer "alguma grade desta linha".
 *
 * Em `above_price_cap` fica a MENOR diferença: a linha exibe o preço do SKU
 * mais barato, e mostrar a diferença de outro contradiria o número ao lado.
 */
export function mergeOutOfScope(
  flagsPerGrade: OutOfScopeFlag[][]
): OutOfScopeFlag[] {
  const byReason = new Map<OutOfScopeReason, OutOfScopeFlag>();
  for (const flags of flagsPerGrade) {
    for (const flag of flags) {
      const current = byReason.get(flag.reason);
      if (
        !current ||
        (flag.amount !== null &&
          (current.amount === null || flag.amount < current.amount))
      ) {
        byReason.set(flag.reason, flag);
      }
    }
  }
  return Array.from(byReason.values());
}
